import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../Header';
import Loading from '../Loading';
import ImageSkeleton from './ImageSkeleton';
import './style.less';
import './lightbox.less';

// Lazy load heavy components
const RowsPhotoAlbum = lazy(() =>
    import('react-photo-album').then((module) => ({ default: module.RowsPhotoAlbum }))
);
const Lightbox = lazy(() => import('yet-another-react-lightbox'));

// Import Captions plugin directly (not lazy loaded since it's a plugin, not a component)
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/plugins/captions.css';
import LoadingLogo from '../LoadingLogo';

// Lazy load CSS
const loadPhotoAlbumCSS = () => import('react-photo-album/rows.css');
const loadLightboxCSS = () => import('yet-another-react-lightbox/styles.css');

interface ImageData {
    attachment_id: number;
    original_filename: string;
    file_type: string;
    file_size: number;
    upload_date: string;
    url: string;
    preview_url?: string;
}

interface TokenData {
    missionary_name?: string;
    missionary_email: string;
    expires_at: string;
    images: ImageData[];
}

const ImageViewer: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [tokenData, setTokenData] = useState<TokenData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [showMetadata, setShowMetadata] = useState(false);
    const [photosWithDimensions, setPhotosWithDimensions] = useState<any[]>([]);
    
    // Test flag for looping lazy load
    const TEST_LOOP_LOAD = new URLSearchParams(window.location.search).get('testloop') === 'true';
    const [testLoadCount, setTestLoadCount] = useState(0);

    useEffect(() => {
        if (token) {
            // Load CSS when component mounts
            loadPhotoAlbumCSS();
            loadLightboxCSS();
            fetchImages();
        }
    }, [token]);
    
    useEffect(() => {
        if (!TEST_LOOP_LOAD) return;
        
        const handleScroll = () => {
            if (loading || !hasMore) return;
            
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            
            if (scrollTop + windowHeight >= documentHeight - 50) {
                loadMore();
            }
        };
        
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loading, TEST_LOOP_LOAD]);

    useEffect(() => {
        if (tokenData?.images) {
            loadImageDimensions();
        }
    }, [tokenData]);
    


    const loadImageDimensions = async () => {
        const imageFiles = tokenData!.images.filter(
            (image) => !image.file_type.startsWith('video/')
        );
        const photosWithDims = await Promise.all(
            imageFiles.map((image) => {
                return new Promise<any>((resolve) => {
                    const img = new Image();
                    const previewSrc = image.preview_url || image.url;
                    img.onload = () => {
                        resolve({
                            src: previewSrc,
                            width: img.naturalWidth,
                            height: img.naturalHeight,
                            alt: image.original_filename
                        });
                    };
                    img.onerror = () => {
                        resolve({
                            src: previewSrc,
                            width: 400,
                            height: 300,
                            alt: image.original_filename
                        });
                    };
                    img.src = previewSrc;
                });
            })
        );
        setPhotosWithDimensions(photosWithDims);
    };

    const fetchImages = async (pageNum = 1) => {
        try {
            const response = await fetch(`/api/images/${token}?page=${pageNum}`);
            if (!response.ok) {
                throw new Error('Invalid or expired token');
            }

            const data = await response.json();

            if (pageNum === 1) {
                setTokenData(data);
            } else {
                if (TEST_LOOP_LOAD && (data.images.length === 0 || pageNum > 1)) {
                    // Loop existing images for testing
                    const existingImages = tokenData?.images || [];
                    if (existingImages.length > 0) {
                        const loopedImages = existingImages.map((img, index) => ({
                            ...img,
                            attachment_id: img.attachment_id + (testLoadCount + 1) * 1000 + index,
                            original_filename: `${testLoadCount + 1}-${img.original_filename}`
                        }));
                        setTokenData((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      images: [...prev.images, ...loopedImages]
                                  }
                                : data
                        );
                        setTestLoadCount(prev => prev + 1);
                        setHasMore(testLoadCount < 10); // Limit to 10 loops
                        setLoading(false);
                        return;
                    }
                }
                
                setTokenData((prev) =>
                    prev
                        ? {
                              ...prev,
                              images: [...prev.images, ...data.images]
                          }
                        : data
                );
            }

            setHasMore(TEST_LOOP_LOAD ? testLoadCount < 10 : data.images.length === 20);
            setLoading(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load images');
            setLoading(false);
        }
    };

    const loadMore = () => {
        if (hasMore && !loading) {
            setPage((prev) => prev + 1);
            fetchImages(page + 1);
        }
    };

    const openLightbox = (index: number) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const photos = photosWithDimensions;

    const lightboxSlides =
        tokenData?.images
            .filter((image) => !image.file_type.startsWith('video/'))
            .map((image) => ({
                src: image.url,
                alt: image.original_filename,
                description: `${image.original_filename} • ${Math.round(image.file_size / 1024)} KB • ${new Date(image.upload_date).toLocaleDateString()}`
            })) || [];

    if (loading && !tokenData) {
        return (
            <div className="image-viewer-page">
                <Header />
                <div className="container">
                    <LoadingLogo />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="image-viewer-page">
                <Header />
                <div className="container">
                    <div className="error-card">
                        <h3>Unable to Load Images</h3>
                        <p>{error}</p>
                        <p>The link may have expired or is invalid.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!tokenData) {
        return null;
    }

    const expiresAt = new Date(tokenData.expires_at);
    const isExpired = expiresAt < new Date();

    if (isExpired) {
        return (
            <div className="image-viewer-page">
                <Header />
                <div className="container">
                    <div className="error-card">
                        <h3>Link Expired</h3>
                        <p>This image viewing link has expired.</p>
                        <p>Please request a new link from your missionary.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="image-viewer-page">
            <Header />
            <div className="container">
                <div className="viewer-header">
                    <h2>{tokenData.missionary_name || 'Missionary'}'s Photos</h2>
                    <p className="viewer-subtitle">From: {tokenData.missionary_email}</p>
                    <p className="expiry-notice">Link expires: {expiresAt.toLocaleDateString()}</p>
                </div>

                {tokenData.images.length === 0 ? (
                    <div className="no-images">
                        <h4>No Images Available</h4>
                        <p>There are no images to display.</p>
                    </div>
                ) : (
                    <>
                        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                            {photos.length > 0 ? (
                                <Suspense
                                    fallback={
                                        <div className="skeleton-grid">
                                            {Array.from({ length: 8 }).map((_, index) => (
                                                <ImageSkeleton key={index} />
                                            ))}
                                        </div>
                                    }
                                >
                                    <RowsPhotoAlbum
                                        photos={photos}
                                        spacing={8}
                                        onClick={({ index }) => openLightbox(index)}
                                    />
                                </Suspense>
                            ) : (
                                <div className="skeleton-grid">
                                    {Array.from({ length: 8 }).map((_, index) => (
                                        <ImageSkeleton key={index} />
                                    ))}
                                </div>
                            )}
                        </div>

                        {lightboxOpen && (
                            <Suspense fallback={<div>Loading...</div>}>
                                <Lightbox
                                    open={lightboxOpen}
                                    close={() => setLightboxOpen(false)}
                                    slides={lightboxSlides}
                                    index={lightboxIndex}
                                    plugins={[Captions]}
                                    captions={{
                                        showToggle: true,
                                        descriptionTextAlign: 'start'
                                    }}
                                    render={{
                                        iconCaptionsVisible: () => (
                                            <span style={{ fontSize: '24px', lineHeight: '1' }}>
                                                ⓘ
                                            </span>
                                        ),
                                        iconCaptionsHidden: () => (
                                            <span
                                                style={{
                                                    fontSize: '24px',
                                                    lineHeight: '1',
                                                    position: 'relative'
                                                }}
                                            >
                                                ⓘ
                                                <span
                                                    style={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        fontSize: '20px',
                                                        color: 'inherit'
                                                    }}
                                                >
                                                    /
                                                </span>
                                            </span>
                                        )
                                    }}
                                />
                            </Suspense>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ImageViewer;
